import { Box, Stack, useToast } from "@chakra-ui/react";
import { Formik, FormikConfig } from "formik";
import { withFormikDevtools } from "formik-devtools-extension";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useGetAccountDetails } from "../api/useGetAccountDetails";
import AccountDetailBar from "../components/AccountDetailBar";
import MarginAccountDetailsSearch, {
  MarginAccountDetailsValues,
} from "../components/MarginAccountDetailsSearch/MarginAccountDetailsSearch";
import marginSearchBarValidation from "../components/MarginAccountDetailsSearch/searchBarValidation";
import MarginAccountDetailsTabs from "../components/MarginAccountDetailsTabs/MarginAccountDetailsTabs";
import { withPermissions } from "../hoc/withPermissions";
import { useAccountLookupStore } from "../hooks/useAccountLookupStore";
import { Permissions } from "../permissions";
import { DEFAULT_ERROR_TOAST } from "../utils/constants";

const MarginAccountDetailsPage = () => {
  const toast = useToast();
  const { accountId: accountIdParam } = useParams();
  const setErrorMessage = useAccountLookupStore(state => state.setErrorMessage);

  const { accountNumber, setAccountNumber, clearAccountLookupStore } =
    useAccountLookupStore();

  const {
    data: accountData,
    isLoading: isLoadingAccount,
    isError: hasAccountErr,
  } = useGetAccountDetails(accountNumber || "", {
    enabled: !!accountNumber,
    onError: res => {
      if (res.response.status >= 500) {
        toast({
          ...DEFAULT_ERROR_TOAST,
          description: res?.response?.data?.errorDescription,
        });
      }
      setErrorMessage("Account not found. Try again.");
    },
  });

  useEffect(() => {
    if (accountIdParam) {
      setAccountNumber(accountIdParam);
    } else {
      clearAccountLookupStore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const formikConfig: FormikConfig<MarginAccountDetailsValues> = {
    initialValues: marginSearchBarValidation.cast({
      accountNumber: accountIdParam || "",
    }) as MarginAccountDetailsValues,
    onSubmit: values => {
      navigate(`/margin-account-details/${values.accountNumber}`);
      setAccountNumber(values.accountNumber);
    },
    validationSchema: marginSearchBarValidation,
    enableReinitialize: true,
    validateOnMount: true,
  };

  return (
    <Stack spacing={0} overflowX="auto" flex="1 1 0" height="100%">
      <Formik {...formikConfig}>
        {formikProps => {
          withFormikDevtools(formikProps);
          return <MarginAccountDetailsSearch isFetching={isLoadingAccount} />;
        }}
      </Formik>
      {!!accountData && (
        <>
          <AccountDetailBar
            accountData={accountData}
            isLoadingAccount={isLoadingAccount}
            hasAccountErr={hasAccountErr}
          />
          <Box p={4} h="full" width="100%" overflow="auto">
            <MarginAccountDetailsTabs />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default withPermissions(MarginAccountDetailsPage, [
  Permissions.AccountRead,
  Permissions.MarginProcessingUiNavView,
]);
